/**
 * A row on the /run page that has not started yet is one of TWO states, and until dimension-98kmm
 * they rendered identically as "Pending — not started" with a running counter:
 *
 *   1. Queued and unclaimed — nothing has picked it up. If that persists, it is an incident.
 *   2. Blocked on a predecessor — the job before it in a chained schedule is still running, so the
 *      dispatcher is correct to hold it back (services/job_queue_service/job_queue_logic.cs:491).
 *      Waiting hours here is normal; a Bulley all-projects sweep runs ~55 minutes.
 *
 * On 2026-08-28 a state-2 row was read as a hung prod integration 15 minutes before a client call.
 * The same conflation had already produced a wrong analytical conclusion (dimension-d1tg0), which
 * is why the queue-latency guidance excludes predecessor_id IS NOT NULL. These helpers are the
 * UI's equivalent guard.
 */

/** Status the back end gives a queued job that has not started (see PortalUtils/PendingJobs.cs).
 * No run of any kind ever reports this, so it is safe to key rendering off it. */
export const PENDING_STATUS = "Pending";

/** The subset of IMessageHeader these helpers read. Kept narrow so tests do not have to build a
 * whole header, and so a `row.original` from react-table satisfies it. */
export interface IPendingFields {
    status: string;
    waiting_on_job_id?: number | null;
    waiting_on_method_info?: string | null;
    waiting_on_job_status?: string | null;
    waiting_on_message_id?: string | null;
}

/** True for any row that is in the queue and has not begun — either of the two states above. */
export function isNotStarted(header: IPendingFields | undefined): boolean {
    return header?.status === PENDING_STATUS;
}

/**
 * True only for state 2. Keyed on waiting_on_job_id rather than on the raw predecessor_id, because
 * the back end deliberately leaves it null when the predecessor row cannot be resolved — naming a
 * job that no longer exists explains nothing.
 */
export function isWaitingOnPredecessor(
    header: IPendingFields | undefined
): boolean {
    return isNotStarted(header) && !!header?.waiting_on_job_id;
}

/**
 * How long the row has been sitting, in words that say WHICH wait it is. The Duration column used
 * to show this bare, which reads as "this ran for 14 minutes" for a job that has not run at all.
 */
export function waitLabel(header: IPendingFields | undefined): string {
    return isWaitingOnPredecessor(header) ? "waiting" : "queued";
}

/**
 * Only the IMMEDIATE predecessor is named, never the whole chain. Chains are routinely 3+ deep on
 * prod — 3,000+ such jobs on 2026-08-27 alone across MATT, BBL, ATLAS and others — so rendering
 * the chain would put an unbounded list in a table cell to answer a question nobody asked. The one
 * job that has to finish next is the whole actionable answer, and following it up the chain is one
 * more click on a page that already links it.
 */
export function predecessorSummary(header: IPendingFields | undefined): string {
    if (!isWaitingOnPredecessor(header)) return "";

    const method = header?.waiting_on_method_info ?? "";
    const status = header?.waiting_on_job_status ?? "";

    return status ? `${method} (${status.toLowerCase()})` : method;
}
